/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useState, useContext, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Tooltip, Popover, Modal } from 'antd';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import { IconPrefix, IconName } from '@fortawesome/pro-solid-svg-icons';
import Cookies from 'universal-cookie';
import { queryCache } from 'react-query';

import IconButton from '~components/_common/IconButton/IconButton';
import SaveTemplateModal from '~components/popups-modals/SaveTemplateModal/SaveTemplateModal';
import ShareModal from '~components/popups-modals/ShareModal/ShareModal';
import LogModal from '~components/popups-modals/LogModal/LogModal';
import { NotificationModal } from '~components/popups-modals/NotificationModal/NotificationModal';
import PrintModal from '~components/popups-modals/PrintModal/PrintModal';
import { RightsModal } from '~components/popups-modals/RightsModal/RightsModal';
import { ProjectSettingsModal } from '~components/popups-modals/ProjectSettingsModal/ProjectSettingsModal';
import { data, dataMobile, dataTimeMachine } from './data';

import doReturnTheme from '~src/styles/themeEngine';
import translate from '~src/i18n/translate';
import AppContext from '~src/contexts/AppContext';
import { EStyleObj } from '~src/types';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
import { useIntl } from 'react-intl';
import { useProjectId } from '~src/hooks/useProjectId';
import { mobileCheck } from '~src/utils/mobileCheck';
import MobileViewContext from '~src/contexts/MobileViewContext';
import { useIsPermissionManager } from '~src/hooks/useIsPermissionManager';
import {
  useIsUserAuthenticated,
  useUserSettings,
} from '~src/hooks/api-hooks/useUserSettings';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { LoadArchiveSettingsReturnType } from '~src/services/api/archiveSettings';
import {
  removeStorageAdminDownload,
  setStorageAdminDownload,
} from '~src/utils/sessionstorageQueries';
import { FileProgressPanelDownload } from '~components/molecules/FileProgressPanelDownload/FileProgressPanelDownload';
import LanguageSettingModal from '~components/popups-modals/LanguageSettingModal/LanguageSettingModal';
import ResourceSetting from '~components/popups-modals/ResourseModal/ResourceSetting.modal';
import { apiCweb, getCookieName } from '~src/services/api/api-cweb';
import { removeAuthCookie } from '~src/hooks/utils/authCookie';
import { useProject } from '~src/hooks/api-hooks/useProject'; // Import API helpers

const Settings = () => {
  const { switcher, currentTheme, themes } = useThemeSwitcher();
  const theme = doReturnTheme(currentTheme);

  // get the App Context (normal, timeMashine, superUser)

  const { appContextState, setAppContextState } = useContext(AppContext);
  const { setShowMobileView } = useContext(MobileViewContext);

  const history = useHistory();
  const isSuperUser = useIsSuperuser();
  const isPermissionManager = useIsPermissionManager();
  const intl = useIntl();
  const userSettings = useUserSettings();
  const archiveSettings = useArchiveSettings();
  const projectId = useProjectId();
  const isOnMyProjectsView = window.location.pathname.startsWith(
    '/my-projects/'
  );
  const isTenderingOn =
    archiveSettings.data?.resArchiveSettings?.is_tendering_on;

  const projectDataFetch = useProject();
  const projectData = projectDataFetch.data;
  // console.log(`projectData`, projectData);

  const cookies = new Cookies();

  const [saveTemplateModaState, setSaveTemplateModaState] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [rightsModal, setRightsModal] = useState(false);
  const [projectSettingsModal, setProjectSettings] = useState(false);
  const [projectResourceModal, setProjectResourceModal] = useState(false);
  const [logModal, setLogModal] = useState(false);
  const [notificationModal, setNotificationModal] = useState(false);
  const [printModal, setPrintModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [languageSettingModal, setLanguageSettingModal] = useState(false);

  const buttonRef = useRef<HTMLDivElement>(null);

  const secondIconAlert: FontAwesomeIconProps['icon'] | null = userSettings.data
    ?.block_notification
    ? ['fas', 'exclamation']
    : userSettings.data?.vacation && userSettings.data?.vacation > Date.now()
    ? ['fas', 'plane']
    : null;

  const isAuthenticated = useIsUserAuthenticated();

  useEffect(() => {
    const hoverEnterHandler = () => {
      setShowTooltip(true);
    };
    const hoverLeftHandler = () => {
      setShowTooltip(false);
    };

    buttonRef.current?.addEventListener('mouseenter', hoverEnterHandler);
    buttonRef.current?.addEventListener('mouseleave', hoverLeftHandler);

    return () => {
      buttonRef.current?.removeEventListener('mouseenter', hoverEnterHandler);
      buttonRef.current?.removeEventListener('mouseleave', hoverLeftHandler);
    };
  }, [buttonRef.current]);

  const openModal = (state, modal) => {
    switch (modal) {
      case 'template':
        setSaveTemplateModaState(state);
        break;
      case 'share':
        setShareModal(state);
        break;
      case 'log':
        setLogModal(state);
        break;
      case 'rights':
        setRightsModal(state);
        break;
      case 'notifications':
        setNotificationModal(state);
        break;
      case 'print':
        setPrintModal(state);
        break;
      case 'project-settings':
        setProjectSettings(state);
        break;
      case 'resource': //project contacts
        setProjectResourceModal(state);
        break;
      case 'language':
        setLanguageSettingModal(state);
        break;
      case 'super-user':
        if (window.sessionStorage.getItem('appState') !== 'superUser') {
          // Entering admin mode
          switcher({ theme: themes.su });
          setAppContextState('super-user');
          window.sessionStorage.removeItem('timeMachineDate');
          window.sessionStorage.setItem('appState', 'superUser');
          history.push(`/projects/${projectId}`);
          setStorageAdminDownload(true);
        } else {
          // Exiting admin mode - check if on Project Builder with unsaved changes
          const isOnProjectBuilder = history.location.pathname.includes(
            '/project-builder'
          );
          const hasUnsavedFolders =
            isOnProjectBuilder &&
            window.sessionStorage.getItem('pb-unsaved-folders') === 'true';

          if (hasUnsavedFolders) {
            Modal.confirm({
              centered: true,
              title: intl.formatMessage({ id: 'you-sure' }),
              content: intl.formatMessage({
                id: 'prevent-navigation-unsaved-folders',
              }),
              onOk: () => {
                // User confirmed - proceed with mode switch
                // Clear unsaved folders state in Project Builder before navigating
                window.dispatchEvent(
                  new CustomEvent('clearProjectBuilderUnsavedFolders')
                );
                window.sessionStorage.setItem('appState', 'normal');
                window.sessionStorage.removeItem('pb-unsaved-folders');
                setAppContextState('normal');
                switcher({ theme: themes.light });

                // Small delay to ensure state is cleared before navigation
                setTimeout(() => {
                  history.push(`/projects/${projectId}`);
                }, 50);
              },
              onCancel: () => {
                // User cancelled - do nothing, stay in admin mode
              },
            });
          } else {
            // No unsaved changes - proceed immediately
            window.sessionStorage.setItem('appState', 'normal');
            setAppContextState('normal');
            switcher({ theme: themes.light });
            history.push(`/projects/${projectId}`);
          }
        }
        break;
      case 'mobile-view':
        window.sessionStorage.setItem('appState', 'normal');
        setShowMobileView(true);
        switcher({ theme: themes.light });
        history.push(`/projects/${projectId}`);
        break;
      case 'login':
        history.push('/login');
        break;
      case 'logout':
        // Remove the cookie
        removeAuthCookie(getCookieName() as string, cookies);
        // Clear React Query cache to remove stale user data
        queryCache.clear();
        // Redirect to login and reload memory stack
        window.location.href = '/login';
        break;
      default:
        break;
    }
  };

  const style: EStyleObj = {
    container: {
      display: 'flex',
      justifyContent: 'center',
      margin: '0px 5px',
    },
    buttonDiv: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '40px',
      height: '40px',
      borderRadius: '20px',
      color: theme.colors.white,
      backgroundColor:
        appContextState === 'time-machine'
          ? theme.colors.darkGray
          : theme.colors.secondary,
      transition: '0.3s',
      cursor: 'pointer',

      ':hover': {
        backgroundColor:
          appContextState === 'time-machine'
            ? theme.colors.lightGray
            : theme.colors.highlightedSecondary,
      },

      ':focus': {
        backgroundColor: theme.colors.highlightedSecondary,
      },
    },
    icons: {
      color: theme.colors.white,
      fontSize: '20px',
      transition: '0.3s',
      outline: 'none',
    },
    red: {
      backgroundColor: theme.colors.red,
    },
  };

  const settingsMenuStyles: EStyleObj = {
    container: {
      display: 'flex',
      flexDirection: 'row',
      padding: '2px 15px',
      cursor: 'pointer',

      ':hover': {
        backgroundColor: theme.colors.primaryLight,
      },
    },
    icon: {
      color: theme.colors.secondary,
      fontSize: '16px',
    },
    text: {
      color: theme.colors.text.primary,
      fontSize: '16px',
      padding: '3px 6px',
    },
    iconWithRedBackground: {
      fontSize: '12px',
      backgroundColor: theme.colors.red,
      borderRadius: '50%',
      padding: '5px',
      marginTop: '2px',
      width: '12px',
    },
  };

  // const clicked = () => {
  //   console.log('clicked');
  // };

  const [dropdownState, setDropdownState] = useState({
    visible: false,
  });

  const handleVisibleChange = (visible: boolean, modal?) => {
    setDropdownState({ visible });

    openModal(true, modal);
  };

  const renderMenuItem = (item) => {
    const isNotificationsOrResource =
      item.modal === 'notifications' || item.modal === 'resource';
    const isDisabledByAuth = !isAuthenticated && isNotificationsOrResource;

    const row = (
      <div
        css={[
          settingsMenuStyles.container,
          isDisabledByAuth && {
            opacity: 0.5,
            cursor: 'not-allowed',
          },
        ]}
        onClick={() => {
          if (isDisabledByAuth) {
            return;
          }
          handleVisibleChange(false, item.modal);
        }}
        role="button"
        tabIndex={0}
        key={item.key}
        className="dark-mode-turn-to-white">
        <IconButton
          iconStyle={item.iconStyle as IconPrefix}
          icon={item.icon as IconName}
          customStyle={settingsMenuStyles.icon}
          backgroundOff
        />

        <span css={settingsMenuStyles.text}>
          {intl.formatMessage({ id: item.name })}
        </span>
        {item.name === 'notifications' && (
          <IconButton
            iconStyle={item.iconStyle as IconPrefix}
            icon={secondIconAlert}
            color={'white'}
            customStyle={[
              settingsMenuStyles.icon,
              settingsMenuStyles.iconWithRedBackground,
            ]}
            backgroundOff
          />
        )}
      </div>
    );

    if (isDisabledByAuth) {
      return (
        <Tooltip
          key={item.key}
          placement="left"
          title={intl.formatMessage({
            id: 'settings-login-to-access',
            defaultMessage: 'Login to access this feature',
          })}>
          {row}
        </Tooltip>
      );
    }

    return row;
  };

  const baseItems = [
    ...(mobileCheck()
      ? dataMobile
      : window.sessionStorage.getItem('appState') === 'timeMachine'
      ? dataTimeMachine
      : data),
  ];

  const content = isSuperUser
    ? baseItems
        /*       .filter(
      (item) =>
        ![
          !archiveSettings?.data?.resArchiveSettings?.is_tags_on &&
            'project-settings-mode',
        ].includes(item.name)
    ) */
        // Remove "Log" outside project and switch Login/Logout based on auth
        .filter((item) => {
          if ((!projectId || isOnMyProjectsView) && item.modal === 'log') {
            return false;
          }

          if (!isTenderingOn && item.modal === 'resource') {
            return false;
          }

          if (isAuthenticated && item.modal === 'login') {
            return false;
          }
          if (!isAuthenticated && item.modal === 'logout') {
            return false;
          }
          return true;
        })
        .map(renderMenuItem)
    : baseItems
        .filter((item) =>
          isPermissionManager
            ? ![
                'super-user-mode',
                'save-template',
                'project-settings-mode',
              ].includes(item.name)
            : ![
                'super-user-mode',
                'save-template',
                'group-rights',
                'project-settings-mode',
              ].includes(item.name)
        )
        // Remove "Log" outside project and switch Login/Logout based on auth
        .filter((item) => {
          if ((!projectId || isOnMyProjectsView) && item.modal === 'log') {
            return false;
          }

          if (!isTenderingOn && item.modal === 'resource') {
            return false;
          }

          if (isAuthenticated && item.modal === 'login') {
            return false;
          }
          if (!isAuthenticated && item.modal === 'logout') {
            return false;
          }
          return true;
        })
        .map(renderMenuItem);

  const exitSu = () => {
    // Check if on Project Builder with unsaved changes
    const isOnProjectBuilder = history.location.pathname.includes(
      '/project-builder'
    );
    const hasUnsavedFolders =
      isOnProjectBuilder &&
      window.sessionStorage.getItem('pb-unsaved-folders') === 'true';

    if (hasUnsavedFolders) {
      Modal.confirm({
        centered: true,
        title: intl.formatMessage({ id: 'you-sure' }),
        content: intl.formatMessage({
          id: 'prevent-navigation-unsaved-folders',
        }),
        onOk: () => {
          // User confirmed - proceed with exit
          // Clear unsaved folders state in Project Builder before navigating
          window.dispatchEvent(
            new CustomEvent('clearProjectBuilderUnsavedFolders')
          );
          window.sessionStorage.setItem('appState', 'normal');
          window.sessionStorage.removeItem('pb-unsaved-folders');
          setAppContextState('normal');
          switcher({ theme: themes.light });

          // Small delay to ensure state is cleared before navigation
          setTimeout(() => {
            history.push(`/projects/${projectId}`);
            removeStorageAdminDownload();
          }, 50);
        },
        onCancel: () => {
          // User cancelled - do nothing, stay in admin mode
        },
      });
    } else {
      // No unsaved changes - proceed immediately
      switcher({ theme: themes.light });
      window.sessionStorage.setItem('appState', 'normal');
      setAppContextState('normal');
      history.push(`/projects/${projectId}`);
      removeStorageAdminDownload();
    }
  };

  return (
    <div css={style.container}>
      {window.sessionStorage.getItem('appState') === 'superUser' ? (
        <Tooltip
          placement="bottom"
          title={translate('super-user-mode-exit')}
          visible={showTooltip}>
          <div
            css={[style.buttonDiv, style.red]}
            onClick={() => exitSu()}
            role="button"
            tabIndex={0}
            ref={buttonRef}>
            <IconButton
              iconStyle="fad"
              icon="user-tie"
              customStyle={style.icons}
              backgroundOff
              // click={() => clicked()}
            />
          </div>
        </Tooltip>
      ) : (
        <Popover
          content={<div>{content}</div>}
          visible={dropdownState.visible}
          onVisibleChange={(visible) => handleVisibleChange(visible)}
          placement="bottomRight"
          trigger="click">
          <Tooltip
            placement="bottom"
            title={translate('settings')}
            visible={showTooltip}>
            <div css={style.buttonDiv} ref={buttonRef}>
              <IconButton
                iconStyle="far"
                icon="ellipsis-v"
                customStyle={style.icons}
                colorSecondIcon={'whie'}
                iconSecond={secondIconAlert}
                customStyleSecondIcon={[
                  settingsMenuStyles.iconWithRedBackground,
                  { marginTop: '12px' },
                ]}
                backgroundOff
                // click={() => clicked()}
              />
            </div>
          </Tooltip>
        </Popover>
      )}

      {/* popup modals */}
      <SaveTemplateModal
        state={saveTemplateModaState}
        onCancel={() => openModal(false, 'template')}
      />
      <ShareModal
        state={shareModal}
        clicked={() => openModal(false, 'share')}
      />
      <LogModal state={logModal} onCancel={() => openModal(false, 'log')} />
      <LanguageSettingModal
        state={languageSettingModal}
        onCancel={() => openModal(false, 'language')}
      />
      <RightsModal
        visible={rightsModal}
        onCancel={() => openModal(false, 'rights')}
      />

      <ProjectSettingsModal
        visible={projectSettingsModal}
        onCancel={() => openModal(false, 'project-settings')}
        projectData={projectData}
      />

      <ResourceSetting
        state={projectResourceModal}
        onCancel={() => openModal(false, 'resource')}
      />

      <NotificationModal
        visible={notificationModal}
        onCancel={() => openModal(false, 'notifications')}
      />

      <PrintModal
        state={printModal}
        clicked={() => openModal(false, 'print')}
      />
      <FileProgressPanelDownload />
    </div>
  );
};

export default Settings;
